import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../utils/AuthContext';
import theme from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const useIsMobile = () => {
  const [m, setM] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
};

const Dashboard = () => {
  const { user, token } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [stats, setStats] = useState({
    // Compteurs de base
    nbDevis: 0,
    nbFactures: 0,
    nbClients: 0,
    nbCollaborateurs: 0,
    nbDemandesNouv: 0,

    // KPIs financiers
    caRealise:        0,
    caEnAttente:      0,   // CA factures émises non payées
    caEnAttenteBrut:  0,
    totalAvoir:       0,
    facturesEnRetard: 0,   // nombre factures en retard
    montantEnRetard:  0,   // montant total en retard
    tauxConversion:   0,   // % devis → factures
    montantDuClient:  0,   // côté client : mon montant restant
  });

  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileData,     setProfileData]     = useState({});
  // eslint-disable-next-line no-unused-vars
  const [loadingStats,    setLoadingStats]    = useState(true);
  const [analyticsTab,  setAnalyticsTab]  = useState('mois');
  const [actionPage,    setActionPage]    = useState(0);
  const touchStartXRef = useRef(null); // useRef évite le stale closure iOS
  const monthScrollRef = useRef(null);
  const [analyticsData, setAnalyticsData] = useState({ mois: [], trim: [], annuel: {} });
  const [showChangeMdp,   setShowChangeMdp]   = useState(false);
  const [mdpForm,         setMdpForm]         = useState({ ancien: '', nouveau: '', confirm: '' });
  const [mdpError,        setMdpError]        = useState('');
  const [mdpSuccess,      setMdpSuccess]      = useState('');
  const [mdpLoading,      setMdpLoading]      = useState(false);
  // Devis en attente de validation côté client
  const [devisEnAttente,  setDevisEnAttente]  = useState([]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    setLoadingStats(true);

    const cfg = { headers: { Authorization: `Bearer ${token}` } };

    // ── Chaque requête dans son propre try/catch ──────────────────────────────
    // Si une API plante, les autres continuent (pas de catch global)
    let devisData    = [];
    let facturesData = [];
    let nbClients    = 0;
    let nbCollaborateurs = 0;
    let nbDemandesNouv   = 0;

    try {
      const res = await axios.get(`${API_URL}/devis`, cfg);
      devisData = res.data.data || res.data || [];
      // Le serveur filtre déjà les devis selon le rôle (client ne voit que les siens)
      // On ne re-filtre PAS côté client pour éviter les problèmes de type id_client
      if (!isAdmin) {
        // Stocker les devis en attente de validation pour la bannière client
        setDevisEnAttente(devisData.filter(d => d.statut === 'envoye'));
      }
    } catch (e) { console.warn('[Dashboard] /devis:', e.message); }

    try {
      const res = await axios.get(`${API_URL}/factures`, cfg);
      facturesData = res.data.data || res.data || [];
      // Serveur filtre déjà selon le rôle
    } catch (e) { console.warn('[Dashboard] /factures:', e.message); }

    if (isAdmin) {
      try {
        const res = await axios.get(`${API_URL}/clients`, cfg);
        nbClients = (res.data.data || res.data || []).length;
      } catch (e) { console.warn('[Dashboard] /clients:', e.message); }

      try {
        const res = await axios.get(`${API_URL}/utilisateurs`, cfg);
        nbCollaborateurs = (res.data.data || res.data || [])
          .filter(u => u.role === 'admin').length;
      } catch (e) { console.warn('[Dashboard] /utilisateurs:', e.message); }

      try {
        const res = await axios.get(`${API_URL}/demandes`, cfg);
        nbDemandesNouv = (res.data.data || res.data || [])
          .filter(d => d.statut === 'nouvelle').length;
      } catch (e) { console.warn('[Dashboard] /demandes:', e.message); }
    }

    // ── Calculs financiers ────────────────────────────────────────────────────
    const now       = new Date();
    const debutMois = new Date(now.getFullYear(), now.getMonth(), 1);

    const facturesEnRetard = facturesData.filter(f => f.statut_paiement === 'en_retard').length;
    const montantEnRetard  = facturesData
      .filter(f => f.statut_paiement === 'en_retard')
      .reduce((s, f) => s + parseFloat(f.montant_restant || 0), 0);

    const nbFactures      = facturesData.length;
    const devisIdsAvecFacture = new Set(facturesData.filter(f=>f.id_devis).map(f=>f.id_devis));
    const nbDevisFactures = devisData.filter(d =>
      ['facture','facture_partielle'].includes(d.statut) || devisIdsAvecFacture.has(d.id_devis)
    ).length;
    const tauxConversion = devisData.length > 0
      ? Math.round((nbDevisFactures / devisData.length) * 100) : 0;
    const montantDuClient = facturesData
      .reduce((s, f) => s + parseFloat(f.montant_restant || 0), 0);

    // ── Notes de crédit ──────────────────────────────────────────────────────
    let notesCredit = [];
    try {
      const res = await axios.get(`${API_URL}/notes-credit`, cfg);
      notesCredit = res.data?.data || res.data || [];
    } catch (e) { console.warn('[Dashboard] /notes-credit:', e.message); }

    const ncParFacture = {};
    notesCredit.forEach(nc => {
      const fid = nc.id_facture;
      const ttcNC = parseFloat(nc.montant||0) * (1 + parseFloat(nc.taux_tva||0)/100);
      ncParFacture[fid] = (ncParFacture[fid]||0) + ttcNC;
    });

    // caRealise calculé ICI après ncParFacture pour pouvoir déduire les NC des factures payées
    const caRealise = facturesData
      .filter(f => f.statut_paiement === 'payee' && new Date(f.date_emission) >= debutMois)
      .reduce((s, f) => s + Math.max(0, parseFloat(f.montant_ttc || 0) - (ncParFacture[f.id_facture] || 0)), 0);

    const caEnAttenteBrut = facturesData
      .filter(f => ['emise','non_payee','partielle','en_retard'].includes(f.statut_paiement))
      .reduce((s,f) => s + parseFloat(f.montant_restant||f.montant_ttc||0), 0);
    const totalNC = facturesData
      .filter(f => ['emise','non_payee','partielle','en_retard'].includes(f.statut_paiement))
      .reduce((s,f) => s + (ncParFacture[f.id_facture]||0), 0);
    const caEnAttente = Math.max(0, caEnAttenteBrut - totalNC);
    const totalAllNC  = Object.values(ncParFacture).reduce((s,v)=>s+v, 0);
    const totalAvoir  = Math.max(0, totalAllNC - totalNC);

    // ── Analytics ─────────────────────────────────────────────────────────────
    let facturesAchat = [];
    if (isAdmin) {
      try {
        const res = await axios.get(`${API_URL}/facturespro`, cfg);
        facturesAchat = (res.data?.data||res.data||[]).filter(fa=>fa.statut!=='ignoree');
      } catch (e) { console.warn('[Dashboard] /facturespro:', e.message); }
    }
    const YEAR = new Date().getFullYear();
    const MOIS_FR  = ['Jan','Fev','Mar','Avr','Mai','Jun','Jul','Aou','Sep','Oct','Nov','Dec'];
    const TRIM_LBL = ['Q1 Jan-Mar','Q2 Avr-Jun','Q3 Jul-Sep','Q4 Oct-Dec'];
    const moisData = MOIS_FR.map((label, m) => {
      const caV = facturesData
        .filter(f => {
          const d = new Date(f.date_emission);
          return d.getFullYear()===YEAR && d.getMonth()===m
            && ['payee','emise','non_payee','partielle','en_retard'].includes(f.statut_paiement);
        })
        .reduce((s,f) => s + Math.max(0, parseFloat(f.montant_ttc||0) - (ncParFacture[f.id_facture]||0)), 0);
      const caA = facturesAchat
        .filter(fa => {
          const d = new Date(fa.date_emission||fa.date_reception);
          return d.getFullYear()===YEAR && d.getMonth()===m;
        })
        .reduce((s,fa) => s + (fa.type_document === 'credit_note' ? -1 : 1) * parseFloat(fa.montant_ttc||0), 0);
      return { label, caV, caA, net: caV - caA };
    });
    const trimData = [0,1,2,3].map(q => ({
      label: TRIM_LBL[q],
      caV: [q*3,q*3+1,q*3+2].reduce((s,i) => s+(moisData[i]?moisData[i].caV:0), 0),
      caA: [q*3,q*3+1,q*3+2].reduce((s,i) => s+(moisData[i]?moisData[i].caA:0), 0),
      net: [q*3,q*3+1,q*3+2].reduce((s,i) => s+(moisData[i]?moisData[i].net:0), 0),
    }));
    const annuelData = {
      caV: moisData.reduce((s,m)=>s+m.caV,0),
      caA: moisData.reduce((s,m)=>s+m.caA,0),
      net: moisData.reduce((s,m)=>s+m.net,0),
    };
    setAnalyticsData({ mois: moisData, trim: trimData, annuel: annuelData });

    setStats({
      nbDevis: devisData.length,
      nbFactures,
      nbClients,
      nbCollaborateurs,
      nbDemandesNouv,
      caRealise,
      caEnAttente,
      caEnAttenteBrut,
      totalAvoir,
      facturesEnRetard,
      montantEnRetard,
      tauxConversion,
      montantDuClient,
    });

    setLoadingStats(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAdmin, user]);

  useEffect(() => {
    if (monthScrollRef.current && analyticsData.mois.length > 0) {
      const currentM = new Date().getMonth();
      const cards = monthScrollRef.current.children;
      if (cards[currentM]) {
        cards[currentM].scrollIntoView({ behavior:'auto', block:'nearest', inline:'start' });
      }
    }
  }, [analyticsData.mois]);

  useEffect(() => {
    fetchStats();
    setProfileData({
      nom:         user?.nom || '',
      prenom:      user?.prenom || '',
      email:       user?.email || '',
      telephone:   user?.telephone || '',
      adresse:     user?.adresse || '',
      ville:       user?.ville || '',
      code_postal: user?.code_postal || '',
    });
  }, [fetchStats, user]);

  const handleSaveProfile = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API_URL}/clients/${user.id_client}`, profileData, config);
      setShowEditProfile(false);
      alert('Profil mis à jour avec succès !');
    } catch {
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleChangeMdp = async () => {
    setMdpError(''); setMdpSuccess('');
    if (!mdpForm.ancien.trim())        return setMdpError('Entrez votre mot de passe actuel.');
    if (mdpForm.nouveau.length < 6)    return setMdpError('Le nouveau mot de passe doit faire au moins 6 caractères.');
    if (mdpForm.nouveau !== mdpForm.confirm) return setMdpError('Les deux mots de passe ne correspondent pas.');
    setMdpLoading(true);
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API_URL}/auth/change-password`, {
        ancien_mot_de_passe:   mdpForm.ancien,
        nouveau_mot_de_passe:  mdpForm.nouveau,
      }, cfg);
      setMdpSuccess('✅ Mot de passe modifié avec succès !');
      setMdpForm({ ancien: '', nouveau: '', confirm: '' });
      setTimeout(() => { setShowChangeMdp(false); setMdpSuccess(''); }, 2500);
    } catch (e) {
      setMdpError(e.response?.data?.message || 'Erreur — vérifiez votre mot de passe actuel.');
    } finally {
      setMdpLoading(false);
    }
  };

  const fmt = (n) => new Intl.NumberFormat('fr-BE', {
    style: 'currency', currency: 'EUR'
  }).format(n);

  // ── RENDU ──────────────────────────────────────────────────────────────────

  return (
    <div style={styles.page}>

      {/* En-tête de page */}
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>
            {isAdmin ? '🔧 Tableau de bord Admin' : '👤 Mon Espace Client'}
          </h1>
          <p style={styles.pageSubtitle}>
            Bienvenue, {user?.prenom} {user?.nom} &nbsp;
            <span style={{
              ...styles.roleBadge,
              background: isAdmin ? theme.primary : theme.success,
            }}>
              {isAdmin ? '👑 Admin' : '👤 Client'}
            </span>
          </p>
        </div>
      </div>

      <div style={styles.content}>

        {/* ── KPIs ADMIN ─────────────────────────────────────────────── */}
        {isAdmin && (
          <>
            {/* KPIs scrollables — 1 ligne, swipe gauche/droite */}
            <div style={{
              display:'flex', gap:'10px', overflowX:'auto',
              scrollSnapType:'x mandatory', WebkitOverflowScrolling:'touch',
              scrollbarWidth:'none', paddingBottom:'4px',
            }}>
              {[
                { icon:'💶', label:'CA ce mois',     value:fmt(stats.caRealise),    color:theme.success||'#0F6E56', bg:theme.successBg||'#F0FDF4' },
                { icon:'⏳', label:'En attente',      value:fmt(stats.caEnAttente),  color:theme.warning||'#E07B00', bg:theme.warningBg||'#FFFBEB' },
                { icon:'↩', label:'Avoirs (NC)',     value:fmt(stats.totalAvoir),   color:'#7c3aed',               bg:'#f5f3ff' },
                { icon:'🔴', label:'Retard paiement', value:stats.facturesEnRetard > 0 ? `${stats.facturesEnRetard} · ${fmt(stats.montantEnRetard)}` : '0',
                  color:stats.facturesEnRetard>0?(theme.danger||'#e74c3c'):(theme.success||'#0F6E56'),
                  bg:stats.facturesEnRetard>0?(theme.dangerBg||'#FEF2F2'):(theme.successBg||'#F0FDF4') },
                { icon:'📈', label:'Conversion',     value:`${stats.tauxConversion}%`, color:theme.primary, bg:theme.primaryBg },
                { icon:'👥', label:'Clients',        value:stats.nbClients,         color:'#e91e63', bg:'#fce4ec' },
                { icon:'👔', label:'Collabs',        value:stats.nbCollaborateurs,  color:'#3f51b5', bg:'#e8eaf6' },
                { icon:'📄', label:'Devis',          value:stats.nbDevis,           color:theme.info, bg:theme.infoBg },
                { icon:'📑', label:'Factures',       value:stats.nbFactures,        color:theme.purple, bg:theme.purpleBg },
              ].map(function(k,i) {
                return (
                  <div key={i} style={{
                    flex:'0 0 calc(33.333% - 7px)', flexShrink:0, scrollSnapAlign:'start',
                    background:k.bg, borderRadius:'12px', padding:'12px 14px',
                    borderTop:'3px solid '+k.color, boxShadow:'0 1px 4px rgba(0,0,0,0.06)',
                    display:'flex', flexDirection:'column', gap:'4px',
                  }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                      <span style={{ fontSize:'18px' }}>{k.icon}</span>
                      <span style={{ fontSize:'9px', fontWeight:'700', color:'#8892a4',
                        textTransform:'uppercase', letterSpacing:'0.05em', lineHeight:1.2 }}>{k.label}</span>
                    </div>
                    <div style={{ fontSize:'clamp(14px,3.5vw,18px)', fontWeight:'800', color:k.color, lineHeight:1.1 }}>{k.value}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── ANALYTICS FINANCIERS ─────────────────────────────────────── */}
        {isAdmin && (
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize:'15px', fontWeight:'700', color:theme.primary,
              margin:'0 0 14px', textTransform:'uppercase', letterSpacing:'0.5px' }}>
              Analyse financiere {new Date().getFullYear()}
            </h2>
            <div style={{ display:'flex', gap:'6px', marginBottom:'14px', flexWrap:'wrap' }}>
              {[['mois','Mensuel'],['trim','Trimestriel'],['annuel','Annuel']].map(function(tab) {
                var key = tab[0]; var lbl = tab[1];
                return (
                  <button key={key} onClick={() => setAnalyticsTab(key)} style={{
                    padding:'7px 16px', borderRadius:'20px', border:'none', cursor:'pointer',
                    fontWeight:'600', fontSize:'13px',
                    background: analyticsTab===key ? theme.primary : theme.primaryBg,
                    color: analyticsTab===key ? 'white' : theme.primary }}>
                    {lbl}
                  </button>
                );
              })}
            </div>

            {analyticsTab === 'mois' && (
              <div>
                {/* Mini bar chart */}
                <div style={{ background:'white', borderRadius:'12px', padding:'16px', marginBottom:'12px',
                  boxShadow:'0 1px 5px rgba(0,0,0,0.06)' }}>
                  <div style={{ display:'flex', gap:'3px', alignItems:'flex-end', height:'80px', marginBottom:'8px' }}>
                    {analyticsData.mois.map(function(m, i) {
                      var maxV = Math.max.apply(null, analyticsData.mois.map(function(x){ return Math.max(x.caV||0, x.caA||0); }));
                      var pV = maxV>0 ? Math.max((m.caV||0)/maxV*76, m.caV>0?4:0) : 0;
                      var pA = maxV>0 ? Math.max((m.caA||0)/maxV*76, m.caA>0?4:0) : 0;
                      var isCur = i===new Date().getMonth();
                      return (
                        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'2px' }}>
                          <div style={{ display:'flex', gap:'2px', alignItems:'flex-end', height:'76px' }}>
                            <div style={{ width:'7px', height:Math.round(pV)+'px', background:theme.success||'#0F6E56',
                              borderRadius:'2px 2px 0 0', opacity:m.caV===0&&m.caA===0?0.2:1,
                              outline:isCur?'2px solid '+(theme.primary||'#1A3A5C'):'none' }} />
                            <div style={{ width:'7px', height:Math.round(pA)+'px', background:theme.danger||'#e74c3c',
                              borderRadius:'2px 2px 0 0', opacity:m.caV===0&&m.caA===0?0.2:1 }} />
                          </div>
                          <div style={{ fontSize:'7px', color:isCur?(theme.primary||'#1A3A5C'):'#8892a4',
                            fontWeight:isCur?'800':'600', textTransform:'uppercase' }}>{m.label.slice(0,3)}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display:'flex', gap:'12px', justifyContent:'center' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'10px', color:theme.textSecondary }}>
                      <div style={{ width:'10px', height:'10px', borderRadius:'2px', background:theme.success||'#0F6E56' }}/>Ventes
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'10px', color:theme.textSecondary }}>
                      <div style={{ width:'10px', height:'10px', borderRadius:'2px', background:theme.danger||'#e74c3c' }}/>Achats
                    </div>
                  </div>
                </div>
                {/* 3 mois visibles + scroll horizontal, positionné sur mois courant */}
                <div ref={monthScrollRef} style={{
                  display:'flex', gap:'8px', overflowX:'auto',
                  scrollSnapType:'x mandatory', WebkitOverflowScrolling:'touch',
                  scrollbarWidth:'none',
                }}>
                  {analyticsData.mois.map(function(m, i) {
                    var net = (m.caV||0) - (m.caA||0);
                    var curM = new Date().getMonth();
                    var isFuture = i > curM;
                    var isCur = i === curM;
                    return (
                      <div key={i} style={{
                        flex:'0 0 calc(33.333% - 6px)', minWidth:'calc(33.333% - 6px)',
                        scrollSnapAlign:'start',
                        background:'white', borderRadius:'10px', padding:'10px 12px',
                        boxShadow:'0 1px 4px rgba(0,0,0,0.06)',
                        opacity: isFuture ? 0.3 : 1,
                        borderTop:'3px solid ' + (isCur?(theme.primary||'#1A3A5C'):net>0?(theme.success||'#0F6E56'):(theme.border||'#e5eaf0')),
                        outline: isCur?('2px solid '+(theme.primary||'#1A3A5C')):'none',
                        outlineOffset:'-1px',
                      }}>
                        <div style={{ fontWeight:'700', fontSize:'10px',
                          color:isCur?(theme.primary||'#1A3A5C'):theme.textSecondary,
                          textTransform:'uppercase', marginBottom:'4px' }}>
                          {m.label}{isCur ? ' ●' : ''}
                        </div>
                        <div style={{ fontSize:'10px', color:theme.success||'#0F6E56', marginBottom:'1px' }}>{fmt(m.caV||0)}</div>
                        <div style={{ fontSize:'10px', color:theme.danger||'#e74c3c', marginBottom:'3px' }}>{fmt(m.caA||0)}</div>
                        <div style={{ fontSize:'12px', fontWeight:'800',
                          color:net>=0?(theme.primary||'#1A3A5C'):(theme.danger||'#e74c3c') }}>
                          {net>=0?'+':''}{fmt(net)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {analyticsTab === 'trim' && (
              <div>
                {/* Mini bar chart trimestriel */}
                <div style={{ background:'white', borderRadius:'12px', padding:'16px', marginBottom:'12px',
                  boxShadow:'0 1px 5px rgba(0,0,0,0.06)' }}>
                  <div style={{ display:'flex', gap:'12px', alignItems:'flex-end', height:'80px', marginBottom:'8px' }}>
                    {analyticsData.trim.map(function(q, i) {
                      var topColors = [theme.info||'#0ea5e9', theme.success||'#0F6E56', '#f59e0b', theme.primary||'#1A3A5C'];
                      var maxV = Math.max.apply(null, analyticsData.trim.map(function(x){ return Math.max(x.caV||0, x.caA||0); }));
                      var pV = maxV>0 ? Math.max((q.caV||0)/maxV*72, q.caV>0?4:0) : 0;
                      var pA = maxV>0 ? Math.max((q.caA||0)/maxV*72, q.caA>0?4:0) : 0;
                      var curQ = Math.floor(new Date().getMonth()/3);
                      var isFuture = i > curQ;
                      return (
                        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'4px' }}>
                          <div style={{ display:'flex', gap:'3px', alignItems:'flex-end', height:'72px', opacity:isFuture?0.3:1 }}>
                            <div style={{ width:'14px', height:Math.round(pV)+'px', background:topColors[i], borderRadius:'3px 3px 0 0' }} />
                            <div style={{ width:'14px', height:Math.round(pA)+'px', background:theme.danger||'#e74c3c', borderRadius:'3px 3px 0 0' }} />
                          </div>
                          <div style={{ fontSize:'9px', fontWeight:'700', color:topColors[i],
                            textTransform:'uppercase', opacity:isFuture?0.4:1 }}>{q.label}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display:'flex', gap:'12px', justifyContent:'center' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'10px', color:theme.textSecondary }}>
                      <div style={{ width:'10px', height:'10px', borderRadius:'2px', background:theme.success||'#0F6E56' }}/>Ventes
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'10px', color:theme.textSecondary }}>
                      <div style={{ width:'10px', height:'10px', borderRadius:'2px', background:theme.danger||'#e74c3c' }}/>Achats
                    </div>
                  </div>
                </div>
                {/* Q1-Q4 en 1 ligne scrollable */}
                <div style={{ display:'flex', gap:'10px', overflowX:'auto',
                  scrollSnapType:'x mandatory', WebkitOverflowScrolling:'touch',
                  scrollbarWidth:'none' }}>
                  {analyticsData.trim.map(function(q, i) {
                    var topColors = [theme.info||'#0ea5e9', theme.success||'#0F6E56', '#f59e0b', theme.primary||'#1A3A5C'];
                    var curQ = Math.floor(new Date().getMonth()/3);
                    var isFuture = i > curQ;
                    var isCur = i === curQ;
                    return (
                      <div key={i} style={{
                        flex:'0 0 calc(25% - 8px)', minWidth:'calc(25% - 8px)',
                        scrollSnapAlign:'start',
                        background:'white', borderRadius:'12px', padding:'14px 12px',
                        boxShadow:'0 1px 5px rgba(0,0,0,0.06)',
                        opacity:isFuture?0.3:1,
                        borderTop:'3px solid '+(isCur?(theme.primary||'#1A3A5C'):topColors[i]),
                        outline:isCur?('2px solid '+(theme.primary||'#1A3A5C')):'none',
                        outlineOffset:'-1px',
                      }}>
                        <div style={{ fontWeight:'800', fontSize:'12px', color:topColors[i],
                          marginBottom:'8px' }}>{q.label}{isCur?' ●':''}</div>
                        <div style={{ fontSize:'10px', color:theme.success||'#0F6E56', marginBottom:'2px' }}>{fmt(q.caV||0)}</div>
                        <div style={{ fontSize:'10px', color:theme.danger||'#e74c3c', marginBottom:'4px' }}>{fmt(q.caA||0)}</div>
                        <div style={{ fontSize:'12px', fontWeight:'800',
                          color:(q.caV||0)-(q.caA||0)>=0?(theme.primary||'#1A3A5C'):(theme.danger||'#e74c3c') }}>
                          {(q.caV||0)-(q.caA||0)>=0?'+':''}{fmt((q.caV||0)-(q.caA||0))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {analyticsTab === 'annuel' && (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'14px' }}>
                  {[
                    { label:'CA Ventes', val:analyticsData.annuel.caV||0, color:theme.success||'#0F6E56', icon:'📈', bg:'#F0FDF4' },
                    { label:'Achats', val:analyticsData.annuel.caA||0, color:theme.danger||'#e74c3c', icon:'📉', bg:'#FEF2F2' },
                    { label:'Résultat net', val:analyticsData.annuel.net||0,
                      color:(analyticsData.annuel.net||0)>=0?(theme.primary||'#1A3A5C'):(theme.danger||'#e74c3c'),
                      icon:(analyticsData.annuel.net||0)>=0?'✅':'⚠️',
                      bg:(analyticsData.annuel.net||0)>=0?'#EFF6FF':'#FFFBEB' },
                  ].map(function(item,i) {
                    return (
                      <div key={i} style={{ background:item.bg, borderRadius:'14px', padding:'16px 12px',
                        boxShadow:'0 2px 8px rgba(0,0,0,0.07)', textAlign:'center' }}>
                        <div style={{ fontSize:'20px', marginBottom:'5px' }}>{item.icon}</div>
                        <div style={{ fontSize:'9px', fontWeight:'700', color:theme.textSecondary,
                          textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'5px' }}>{item.label}</div>
                        <div style={{ fontSize:'clamp(13px,3vw,19px)', fontWeight:'800', color:item.color }}>{fmt(item.val)}</div>
                      </div>
                    );
                  })}
                </div>
                {(function() {
                  var total = Math.max(analyticsData.annuel.caV||0, 1);
                  var pctV = Math.min(100, ((analyticsData.annuel.caV||0)/total)*100);
                  var pctA = Math.min(100, ((analyticsData.annuel.caA||0)/total)*100);
                  return (
                    <div style={{ background:'white', borderRadius:'10px', padding:'14px 16px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                      <div style={{ fontSize:'11px', fontWeight:'600', color:theme.textSecondary, marginBottom:'10px' }}>
                        Rapport Ventes / Achats — {new Date().getFullYear()}
                      </div>
                      <div style={{ marginBottom:'10px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', marginBottom:'4px' }}>
                          <span style={{ color:theme.success||'#0F6E56', fontWeight:'600' }}>Ventes</span>
                          <span>{Math.round(pctV)}%</span>
                        </div>
                        <div style={{ height:'8px', borderRadius:'4px', background:'#f0f4f8', overflow:'hidden' }}>
                          <div style={{ height:'100%', width:pctV+'%', background:theme.success||'#0F6E56', borderRadius:'4px' }}/>
                        </div>
                      </div>
                      <div>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', marginBottom:'4px' }}>
                          <span style={{ color:theme.danger||'#e74c3c', fontWeight:'600' }}>Achats</span>
                          <span>{Math.round(pctA)}%</span>
                        </div>
                        <div style={{ height:'8px', borderRadius:'4px', background:'#f0f4f8', overflow:'hidden' }}>
                          <div style={{ height:'100%', width:pctA+'%', background:theme.danger||'#e74c3c', borderRadius:'4px' }}/>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* ── KPIs CLIENT ────────────────────────────────────────────── */}
        {!isAdmin && (
          <>
            {/* KPIs client scrollables — 1 ligne swipe */}
            <div style={{
              display:'flex', gap:'10px', overflowX:'auto',
              scrollSnapType:'x mandatory', WebkitOverflowScrolling:'touch',
              scrollbarWidth:'none', paddingBottom:'4px',
            }}>
              {[
                { icon:'📄', label:'Mes devis',     value:stats.nbDevis,                color:theme.info||'#0ea5e9',   bg:theme.infoBg||'#EFF6FF' },
                { icon:'📑', label:'Mes factures',  value:stats.nbFactures,             color:theme.purple||'#7c3aed', bg:theme.purpleBg||'#F5F3FF' },
                { icon:'💶', label:'Montant dû',    value:fmt(stats.montantDuClient),   color:theme.danger||'#e74c3c', bg:theme.dangerBg||'#FEF2F2' },
              ].map(function(k,i) {
                return (
                  <div key={i} style={{
                    flex:'0 0 calc(33.333% - 7px)', flexShrink:0, scrollSnapAlign:'start',
                    background:k.bg, borderRadius:'12px', padding:'12px 14px',
                    borderTop:'3px solid '+k.color, boxShadow:'0 1px 4px rgba(0,0,0,0.06)',
                    display:'flex', flexDirection:'column', gap:'4px',
                  }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                      <span style={{ fontSize:'18px' }}>{k.icon}</span>
                      <span style={{ fontSize:'9px', fontWeight:'700', color:'#8892a4',
                        textTransform:'uppercase', letterSpacing:'0.05em' }}>{k.label}</span>
                    </div>
                    <div style={{ fontSize:'clamp(14px,3.5vw,18px)', fontWeight:'800', color:k.color }}>{k.value}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── DEVIS EN ATTENTE DE VALIDATION (client) ─────────────────────── */}
        {!isAdmin && devisEnAttente.length > 0 && (
          <div style={{ ...styles.section, background:'linear-gradient(135deg,#fff8e1,#fff3e0)',
            border:'2px solid #f9a825', borderRadius:'14px', padding:'clamp(12px,3vw,20px)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'12px', flexWrap:'wrap' }}>
              <div style={{ width:'38px', height:'38px', borderRadius:'50%', background:'#e65100', flexShrink:0,
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px' }}>✍️</div>
              <div>
                <div style={{ fontWeight:'800', fontSize:'14px', color:'#e65100' }}>
                  Action requise — {devisEnAttente.length} devis à valider
                </div>
                <div style={{ fontSize:'11px', color:'#b45309' }}>Appuyez pour consulter et signer</div>
              </div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {devisEnAttente.map(function(d) {
                var expired = d.date_validite && new Date(d.date_validite) < new Date();
                return (
                  <div key={d.id_devis} style={{
                    background:'white', borderRadius:'10px', padding:'12px 14px',
                    border:'1px solid '+(expired?'#ffccbc':'#ffe082'),
                    display:'flex', alignItems:'center', gap:'10px',
                    boxShadow:'0 1px 4px rgba(0,0,0,0.06)',
                  }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:'700', fontSize:'14px', color:'#333',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        📄 {d.numero_devis}
                      </div>
                      <div style={{ fontSize:'12px', color:'#888', marginTop:'2px', display:'flex', gap:'8px', flexWrap:'wrap' }}>
                        <span style={{ color:'#2D6A9F', fontWeight:'600' }}>
                          {parseFloat(d.montant_ttc||0).toLocaleString('fr-BE',{style:'currency',currency:'EUR'})}
                        </span>
                        {d.date_validite && (
                          <span style={{ color:expired?'#f44336':'#888' }}>
                            {expired?'⚠️ Expiré':'·'} {new Date(d.date_validite).toLocaleDateString('fr-BE')}
                          </span>
                        )}
                      </div>
                    </div>
                    <button onClick={function(){ navigate('/devis?valider='+d.id_devis); }}
                      style={{ background:'#e65100', color:'white', border:'none', borderRadius:'8px',
                        padding:'10px 16px', fontSize:'13px', fontWeight:'700', cursor:'pointer',
                        flexShrink:0, whiteSpace:'nowrap' }}>
                      ✅ Valider
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── ACTIONS RAPIDES ─────────────────────────────────────────── */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📁 Actions rapides</h2>
          {/* ── Carousel 4 actions/page + swipe ── */}
          {(function() {
            var adminList = [
              { href:'/demandes',       icon:'📬', label:'Demandes',   desc:'Traiter',   bg:'#FEF2F2', icoBg:'#FFE4E6' },
              { href:'/devis',          icon:'📄', label:'Devis',      desc:'Gérer',     bg:'#EFF6FF', icoBg:'#DBEAFE' },
              { href:'/factures',       icon:'📑', label:'Factures',   desc:'Vente',     bg:'#F5F3FF', icoBg:'#EDE9FE' },
              { href:'/clients',        icon:'👥', label:'Clients',    desc:'Gérer',     bg:'#FFF1F2', icoBg:'#FFE4E6' },
              { href:'/collaborateurs', icon:'👔', label:'Collabs',    desc:'Équipe',    bg:'#EFF6FF', icoBg:'#BFDBFE' },
              { href:'/courriers',      icon:'📝', label:'Courriers',  desc:'& Docs',    bg:'#FFFBEB', icoBg:'#FEF3C7' },
              { href:'/facturespro',    icon:'📥', label:'Achat',      desc:'Peppol',    bg:'#F0F9FF', icoBg:'#E0F2FE' },
              { href:'/notes-frais',    icon:'🧾', label:'Frais',      desc:'OCR',       bg:'#F0FDF4', icoBg:'#DCFCE7' },
              { href:'/deplacements',   icon:'🚗', label:'Trajets',    desc:'km',        bg:'#F5F3FF', icoBg:'#DDD6FE' },
              { href:'/produits',       icon:'📂', label:'Catalogue',  desc:'Services',  bg:'#F0FDF4', icoBg:'#BBF7D0' },
            ];
            var clientList = [
              { href:'/produits',  icon:'🛒', label:'Commander',   desc:'Service',    bg:'#EFF6FF', icoBg:'#DBEAFE' },
              { href:'/devis',     icon:'📄', label:'Mes devis',   desc:'Consulter',  bg:'#F0FDF4', icoBg:'#DCFCE7' },
              { href:'/factures',  icon:'📑', label:'Factures',    desc:'Consulter',  bg:'#F5F3FF', icoBg:'#EDE9FE' },
              { href:'/paiements', icon:'💳', label:'Paiements',   desc:'Historique', bg:'#FFFBEB', icoBg:'#FEF3C7' },
              { href:'', icon:'✏️', label:'Mon profil', desc:'Modifier', bg:'#FEF9EC', icoBg:'#FDE68A',
                onClick:function(){ setShowEditProfile(true); } },
              { href:'', icon:'🔑', label:'Mot de passe', desc:'Changer', bg:'#F0FDF4', icoBg:'#DCFCE7',
                onClick:function(){ setShowChangeMdp(true); setMdpError(''); setMdpSuccess(''); } },
            ];
            var list = isAdmin ? adminList : clientList;
            var PER = 4;
            var total = Math.ceil(list.length / PER);
            var visible = list.slice(actionPage * PER, (actionPage + 1) * PER);
            return (
              <div
                onTouchStart={function(e){ touchStartXRef.current = e.touches[0].clientX; }}
                onTouchEnd={function(e){
                  if (touchStartXRef.current === null) return;
                  var dx = touchStartXRef.current - e.changedTouches[0].clientX;
                  if (dx > 50) setActionPage(function(p){ return Math.min(total-1, p+1); });
                  else if (dx < -50) setActionPage(function(p){ return Math.max(0, p-1); });
                  touchStartXRef.current = null;
                }}
              >
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px', marginBottom:'10px' }}>
                  {visible.map(function(a, idx) {
                    return <ActionCard key={idx} href={a.href} icon={a.icon} label={a.label}
                      desc={a.desc} bg={a.bg} icoBg={a.icoBg} onClick={a.onClick} />;
                  })}
                </div>
                {total > 1 && (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                    <button onClick={function(){ setActionPage(function(p){ return Math.max(0,p-1); }); }}
                      disabled={actionPage===0}
                      style={{ width:'30px', height:'30px', borderRadius:'50%', border:'none',
                        background:actionPage===0?'#f0f4f8':theme.primary,
                        color:actionPage===0?'#bbb':'white',
                        cursor:actionPage===0?'default':'pointer', fontSize:'16px',
                        display:'flex', alignItems:'center', justifyContent:'center' }}>‹</button>
                    {Array.from({length:total}).map(function(_,p){
                      return <div key={p} style={{ width:p===actionPage?'20px':'8px', height:'8px',
                        borderRadius:'4px', background:p===actionPage?theme.primary:'#d1dae6',
                        transition:'width 0.2s' }}/>;
                    })}
                    <button onClick={function(){ setActionPage(function(p){ return Math.min(total-1,p+1); }); }}
                      disabled={actionPage===total-1}
                      style={{ width:'30px', height:'30px', borderRadius:'50%', border:'none',
                        background:actionPage===total-1?'#f0f4f8':theme.primary,
                        color:actionPage===total-1?'#bbb':'white',
                        cursor:actionPage===total-1?'default':'pointer', fontSize:'16px',
                        display:'flex', alignItems:'center', justifyContent:'center' }}>›</button>
                  </div>
                )}
              </div>
            );
          })()}
          </div>
        </div>


      {/* ── MODAL MODIFIER PROFIL ───────────────────────────────────── */}
      {showEditProfile && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={{ margin: '0 0 20px 0', color: theme.primary }}>
              ✏️ Modifier mes informations
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                { key: 'nom',         label: 'Nom',         type: 'text'  },
                { key: 'prenom',      label: 'Prénom',      type: 'text'  },
                { key: 'email',       label: 'Email',       type: 'email' },
                { key: 'telephone',   label: 'Téléphone',   type: 'text'  },
                { key: 'adresse',     label: 'Adresse',     type: 'text'  },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>
                    {f.label}
                  </label>
                  <input
                    type={f.type}
                    value={profileData[f.key] || ''}
                    onChange={e => setProfileData({ ...profileData, [f.key]: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: `1px solid ${theme.border}`, borderRadius: '7px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                {[
                  { key: 'ville',       label: 'Ville' },
                  { key: 'code_postal', label: 'Code postal' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>
                      {f.label}
                    </label>
                    <input
                      type="text"
                      value={profileData[f.key] || ''}
                      onChange={e => setProfileData({ ...profileData, [f.key]: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: `1px solid ${theme.border}`, borderRadius: '7px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button onClick={handleSaveProfile} style={{
                flex: 1, padding: '12px', background: theme.gradientPrimary,
                color: 'white', border: 'none', borderRadius: '8px',
                cursor: 'pointer', fontWeight: '600', fontSize: '15px'
              }}>
                💾 Sauvegarder
              </button>
              <button onClick={() => setShowEditProfile(false)} style={{
                flex: 1, padding: '12px', background: '#95a5a6',
                color: 'white', border: 'none', borderRadius: '8px',
                cursor: 'pointer', fontWeight: '600', fontSize: '15px'
              }}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL CHANGER MOT DE PASSE ──────────────────────────────── */}
      {showChangeMdp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}
          onClick={e => e.target === e.currentTarget && setShowChangeMdp(false)}>
          <div style={{ background: 'white', borderRadius: '14px', padding: '28px',
            maxWidth: '420px', width: '96%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <h2 style={{ margin: '0 0 6px', fontSize: '18px', color: '#2c3e50' }}>
              🔑 Changer mon mot de passe
            </h2>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#666' }}>
              Choisissez un mot de passe sécurisé d'au moins 6 caractères.
            </p>

            {mdpError && (
              <div style={{ background: '#fff5f5', color: '#e53e3e', padding: '10px 14px',
                borderRadius: '8px', marginBottom: '14px', fontSize: '13px', border: '1px solid #fed7d7' }}>
                ⚠️ {mdpError}
              </div>
            )}
            {mdpSuccess && (
              <div style={{ background: '#f0fff4', color: '#38a169', padding: '10px 14px',
                borderRadius: '8px', marginBottom: '14px', fontSize: '13px', border: '1px solid #c6f6d5' }}>
                {mdpSuccess}
              </div>
            )}

            {[
              { key: 'ancien',  label: 'Mot de passe actuel',    ph: '••••••••'          },
              { key: 'nouveau', label: 'Nouveau mot de passe',    ph: 'Min. 6 caractères' },
              { key: 'confirm', label: 'Confirmer le nouveau',    ph: 'Répétez le nouveau'},
            ].map(f => (
              <div key={f.key} style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '12px',
                  color: '#666', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {f.label}
                </label>
                <input type="password" value={mdpForm[f.key]}
                  onChange={e => setMdpForm(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.ph}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #dce8f0',
                    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                    outline: 'none', color: '#2c3e50', background: '#fafafa' }} />
              </div>
            ))}

            <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
              <button onClick={handleChangeMdp} disabled={mdpLoading}
                style={{ flex: 1, padding: '11px', background: '#4f6d89', color: 'white',
                  border: 'none', borderRadius: '8px', fontWeight: '600',
                  cursor: mdpLoading ? 'not-allowed' : 'pointer', fontSize: '14px', opacity: mdpLoading ? 0.7 : 1 }}>
                {mdpLoading ? '⏳ Modification…' : '💾 Modifier le mot de passe'}
              </button>
              <button onClick={() => { setShowChangeMdp(false); setMdpError(''); setMdpSuccess(''); }}
                style={{ padding: '11px 18px', background: '#f0f0f0', color: '#666',
                  border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Sous-composants ────────────────────────────────────────────────────────────

const KpiCard = ({ icon, label, value, color, bg, alert }) => (
  <div style={{
    background: 'white',
    padding: '14px 16px',
    borderRadius: theme.radiusMd,
    boxShadow: theme.shadow,
    borderLeft: '4px solid ' + color,
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    overflow: 'hidden',
    minWidth: 0,
  }}>
    <div style={{
      width: '44px', height: '44px', minWidth: '44px',
      background: bg, borderRadius: '10px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '20px', flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
      <p style={{ margin: 0, color: '#666', fontSize: '9.5px', fontWeight: '600',
        textTransform: 'uppercase', letterSpacing: '0.03em',
        lineHeight: 1.35, overflowWrap: 'break-word', wordBreak: 'normal',
        whiteSpace: 'normal', hyphens: 'auto' }}>
        {label}
      </p>
      <p style={{ margin: '4px 0 0 0', fontSize: '17px', fontWeight: '700',
        color: color, lineHeight: 1.2, overflowWrap: 'break-word' }}>
        {value}
      </p>
    </div>
  </div>
);

const ActionCard = ({ href, icon, label, desc, bg, icoBg, badge, onClick }) => {
  const nav = useNavigate();
  const handleClick = () => { if (onClick) { onClick(); return; } if (href) nav(href); };
  return (
    <div onClick={handleClick} style={{
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      gap:'8px', padding:'14px 8px', background:bg||'white', borderRadius:'12px',
      boxShadow:'0 1px 5px rgba(0,0,0,0.07)', position:'relative', cursor:'pointer',
      minHeight:'88px',
    }}>
      <div style={{ width:'44px', height:'44px', background:icoBg||'#f0f4f8',
        borderRadius:'12px', display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:'20px', boxShadow:'0 2px 5px rgba(0,0,0,0.08)' }}>
        {icon}
      </div>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontWeight:'700', fontSize:'11px', color:'#2c3e50' }}>{label}</div>
        {desc && <div style={{ fontSize:'9px', color:'#8892a4', marginTop:'1px' }}>{desc}</div>}
      </div>
      {badge > 0 && (
        <span style={{ position:'absolute', top:'-5px', right:'-5px', background:'#e74c3c',
          color:'white', borderRadius:'50%', width:'18px', height:'18px',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:'9px', fontWeight:'700' }}>
          {badge>9?'9+':badge}
        </span>
      )}
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: theme.pageBg,
  },
  pageHeader: {
    background: theme.gradientHeader,
    padding: 'clamp(12px,4vw,24px) clamp(14px,4vw,32px)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pageTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: '700',
    color: 'white',
  },
  pageSubtitle: {
    margin: '6px 0 0 0',
    fontSize: '14px',
    opacity: 0.9,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  roleBadge: {
    padding: '2px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    color: 'white',
    fontWeight: '600',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: 'clamp(12px,3vw,28px) clamp(10px,3vw,24px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))',
    gap: '16px',
  },
  section: {
    background: 'white',
    padding: '24px',
    borderRadius: theme.radiusMd,
    boxShadow: theme.shadow,
  },
  sectionTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: '700',
    color: theme.textPrimary,
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: '14px',
  },
  actionCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    borderRadius: '10px',
    textDecoration: 'none',
    color: '#333',
  },
  actionLabel: {
    fontWeight: '600',
    fontSize: '14px',
  },
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'white',
    padding: '32px',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '85vh',
    overflowY: 'auto',
    boxShadow: theme.shadowLg,
  },
};

export default Dashboard;
